import React, { useState, useEffect } from 'react';
import { Box, Flex, useDisclosure, Drawer, DrawerContent } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { Header, Sidebar } from '../Components/dashboard'; 
import { useAuth } from '../Contexts/AuthContext';
import { useLang } from '../Hooks';

const DashboardLayout = ({ children }) => {
    const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [pageTitle, setPageTitle] = useState("Dashboard");
    const { logout } = useAuth();
    const { lang, texts } = useLang();
    const location = useLocation();
    
    const toggleSidebarCollapse = () => setIsSidebarCollapsed(!isSidebarCollapsed);

    useEffect(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        let titleKey = 'dashboard';
        if (pathSegments.length > 1) {
            const lastSegment = pathSegments[pathSegments.length - 1];
            const matchedKey = Object.keys(texts.sidebar || {}).find(key => 
                lastSegment.toLowerCase() === key.toLowerCase().replace(/&|\s/g, ''));
            if (matchedKey) titleKey = matchedKey;
        }
        setPageTitle(texts.sidebar?.[titleKey] || "Dashboard");
    }, [location.pathname, texts.sidebar, lang]);

    const sidebarBaseWidth = "260px";
    const sidebarCollapsedWidth = "80px";
    const currentDesktopSidebarWidth = isSidebarCollapsed ? sidebarCollapsedWidth : sidebarBaseWidth;

    const marginProperty = lang === 'ar' ? 'mr' : 'ml';
    const sidebarPosition = lang === 'ar' ? 'right' : 'left';

    return (
        <Flex 
            h="100dvh" 
            w="100dvw" 
            overflow="auto" 
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
            // UPDATED: Using CSS variables via sx prop
            sx={{
                bg: 'var(--layout-bg)'
            }}
        >
            <Box
                display={{ base: 'none', md: 'block' }}
                w={currentDesktopSidebarWidth}
                transition="width 0.2s ease-in-out"
                flexShrink={0}
                h="100dvh"
                position="fixed"
                top="0"
                left={sidebarPosition === 'left' ? '0' : 'auto'}
                right={sidebarPosition === 'right' ? '0' : 'auto'}
                zIndex="sticky"
                // The background color is now controlled by the Sidebar component itself
            >
                <Sidebar
                    isCollapsed={isSidebarCollapsed}
                    onToggleCollapse={toggleSidebarCollapse}
                    currentLang={lang}
                    onLogout={logout}
                    onCloseDrawer={() => {}}
                />
            </Box>
            <Drawer isOpen={isDrawerOpen} placement={sidebarPosition} onClose={onDrawerClose}>
                <DrawerContent 
                    sx={{
                        bg: 'var(--drawer-bg)',
                        color: 'var(--drawer-text)'
                    }}
                >
                    <Sidebar 
                        isCollapsed={false}
                        onToggleCollapse={() => {}} 
                        currentLang={lang} 
                        onLogout={logout} 
                        onCloseDrawer={onDrawerClose} 
                    />
                </DrawerContent>
            </Drawer>

            <Flex 
                direction="column" 
                flex="1" 
                sx={{
                    [marginProperty]: { base: 0, md: currentDesktopSidebarWidth }
                }}
                transition="margin 0.2s ease-in-out"
                h="100dvh" 
            >
                <Header 
                    onOpenSidebar={onDrawerOpen} 
                    pageTitle={pageTitle} 
                    onLogout={logout} 
                />
                
                <Box 
                  as="main" 
                  flex={1}
                  overflowY="hidden" 
                  overflowX="hidden"  
                  minH={0} 
                  h={'calc(100dvh - 3rem)'}
                  w={{
                    base : "100%",
                    md : `calc(100dvw - ${currentDesktopSidebarWidth} - 3px)`
                  }}
                  className="vvvvv-1"
                  p={1}
                  sx={{
                    bg: 'var(--layout-main-content-bg)',
                    color: 'var(--layout-main-content-text)'
                  }}
                >
                    {children}
                </Box>
            </Flex>
        </Flex>
    );
};

export default DashboardLayout;